import React, { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { LABELS } from "../../../../app/constants/TextConstants";
import { SettingsInput } from "../../../../components/inputs/SettingsInput";
import { FieldLabel } from "../../../../components/newUI/text";
import SearchContacts from "../../../../components/searchContacts";
import { SelectContacts } from "../../../../components/select/SelectContacts";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useAddOrgContactMutation,
  useEditOrgContactMutation,
  useGetAddOrganizationFormDataQuery,
  useGetPhoneTypesQuery,
  useLazyGetOrganizationSinQuery,
  useLazyGetOrgContactQuery,
} from "../../../../slices/ContactsSlice";
import { useAddCustomerTagsMutation } from "../../../../slices/ContactTagsSlice";
import { useAddOrganizationLinkedProfileMutation } from "../../../../slices/LinkedProfilesSlice";
import { SelectOptionType } from "../../../../types/types";
import { validateEmail } from "../../../../utils/email";
import { useDuplicateEmailWarning } from "../../duplicateEmail/useDuplicateEmailWarning";
import {
  duplicateBusinessIdText,
  isAllowedNumberBoxKey,
  NUMBER_CHECK_FAILED,
  numberCheckResult,
} from "../../utils/duplicateNumber";
import {
  businessIdMaxLength,
  businessIdProblem,
  formatBusinessId,
  isCompleteBusinessId,
} from "../../../region/region";
import { useFirmRegion } from "../../../region/useFirmRegion";
import { personNameProblem } from "../../utils/nameRules";
import { phoneNumberProblem } from "../../utils/phoneNumber";
import { relationshipProblem } from "../../utils/relationship";
import {
  ContactKind,
  ContactTypeSelector,
  formatPhoneNumber,
  parseMutationError,
} from "./addContactForm.helpers";
import { AddContactFormFooter } from "./components/AddContactFormFooter";
import { AddContactFormHeader } from "./components/AddContactFormHeader";
import { AddContactSection } from "./components/AddContactSection";
import { ContactTagsSelector } from "./ContactTagsSelector";
import { ADD_CONTACT_INPUT_CLASSES } from "./model/addContact.constants";
import {
  buildOrganizationUpdatePayload,
  getNameParts,
  getPrimaryContactPayload,
  hasPrimaryContactData,
} from "./utils/organizationContactPayload";

type Props = {
  setIsLoading?: (isLoading: boolean) => void;
  closeModal: () => void;
  closeAddCustomerOrganizationPopupModal: () => void;
  isSearch?: boolean;
  setContactsForSort?: (newContact: {
    id: string;
    name: string;
    isBusinessContact: boolean;
    customerId?: string;
  }) => void;
  /** Pre-filled form (Ask ProCharted actions); nothing is created until Create. */
  initialValues?: Partial<
    Pick<
      FormData,
      | "companyName"
      | "primaryContactName"
      | "primaryContactPhone"
      | "primaryContactEmail"
    >
  > & { contactType?: ContactKind };
  /** Called after a successful Create, before the window navigates. */
  onCreated?: (created: {
    id: string;
    customerId: string;
    name: string;
  }) => void;
};

type FormData = {
  companyName: string;
  businessNumber: string;
  primaryContactName: string;
  primaryContactPhone: string;
  primaryContactEmail: string;
  linkedProfile: SelectOptionType | null;
  linkedProfileRelation: string;
  tags: SelectOptionType[];
};

type NewOrganizationContact = {
  id?: string;
  customerId?: string;
  companyName?: string;
};

const AddOrganizationView: React.FC<Props> = ({
  closeModal,
  closeAddCustomerOrganizationPopupModal,
  setContactsForSort,
  isSearch,
  initialValues,
  onCreated,
}) => {
  const navigate = useNavigate();
  const linkedFirm = getLinkedFirm();
  const [contactType, setContactType] = useState<ContactKind>(
    initialValues?.contactType || "Client",
  );
  const [duplicateBusinessNumberError, setDuplicateBusinessNumberError] =
    useState("");
  const region = useFirmRegion();
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const { data: organizationAdd } = useGetAddOrganizationFormDataQuery();
  const { data: phoneTypes = [] } = useGetPhoneTypesQuery();

  const [addOrgContact, { error: errorAdd }] = useAddOrgContactMutation();
  const [addCustomerTags] = useAddCustomerTagsMutation();
  const [addOrganizationLinkedProfile] =
    useAddOrganizationLinkedProfileMutation();
  const [getOrgContact] = useLazyGetOrgContactQuery();
  const [editOrgContact] = useEditOrgContactMutation();
  const [getExistSin] = useLazyGetOrganizationSinQuery();
  const { confirmEmails, dialog: duplicateEmailDialog } =
    useDuplicateEmailWarning();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      companyName: initialValues?.companyName || "",
      businessNumber: "",
      primaryContactName: initialValues?.primaryContactName || "",
      primaryContactPhone: initialValues?.primaryContactPhone || "",
      primaryContactEmail: initialValues?.primaryContactEmail || "",
      linkedProfile: null,
      linkedProfileRelation: "",
      tags: [],
    },
  });

  const selectedContactTypeId = useMemo(() => {
    return organizationAdd?.contactTypes.find(
      (type) => type.contactType === contactType,
    )?.id;
  }, [contactType, organizationAdd?.contactTypes]);

  const onSubmit = async (data: FormData) => {
    setDuplicateBusinessNumberError("");
    setIsSubmittingContact(true);

    const orgId = linkedFirm?.orgId?.toUpperCase();
    const businessNumber = data.businessNumber.trim().toUpperCase();

    if (isCompleteBusinessId(region.code, businessNumber)) {
      try {
        await getExistSin({ orgId, sin: "", businessNumber }).unwrap();
      } catch (error) {
        setDuplicateBusinessNumberError(
          numberCheckResult(error) === "duplicate"
            ? duplicateBusinessIdText(region.businessId.label)
            : NUMBER_CHECK_FAILED,
        );
        setIsSubmittingContact(false);
        return;
      }
    }

    // Roadmap 1784: warn (never block) when another client uses the contact
    // person's email.
    if (!(await confirmEmails([data.primaryContactEmail]))) {
      setIsSubmittingContact(false);
      return;
    }

    const primaryContactPayload = getPrimaryContactPayload(data);
    const primaryContact = getNameParts(data.primaryContactName);

    try {
      const newOrgContact = (await addOrgContact({
        companyName: data.companyName.trim(),
        businessNumber: data.businessNumber,
        contactTypeEnumId: selectedContactTypeId,
        locationId: linkedFirm?.locationId || null,
        contactStatusEnumId: organizationAdd?.contactStatusType.id,
        contactPersons: primaryContactPayload,
        contactPersonFirstName: primaryContact.firstName,
        contactPersonLastName: primaryContact.lastName,
        contactPersonEmailAddress: [],
        contactPersonPhoneNumber: [],
      }).unwrap()) as NewOrganizationContact;

      try {
        if (newOrgContact.customerId && data.tags.length > 0) {
          await addCustomerTags({
            customerId: newOrgContact.customerId,
            tagIds: data.tags.map((tag) => tag.value),
          }).unwrap();
        }

        if (newOrgContact.id && data.linkedProfile?.value) {
          await addOrganizationLinkedProfile({
            businessContactId: newOrgContact.id,
            linkedCustomerId: data.linkedProfile.value,
            relation: data.linkedProfileRelation,
          }).unwrap();
        }

        if (
          newOrgContact.id &&
          linkedFirm?.orgId &&
          hasPrimaryContactData(data)
        ) {
          const organization = await getOrgContact({
            orgId: linkedFirm.orgId,
            id: newOrgContact.id,
          }).unwrap();

          await editOrgContact(
            buildOrganizationUpdatePayload(
              organization,
              data,
              phoneTypes[0]?.value,
            ),
          ).unwrap();
        }
      } catch {
        // Organization is already created. Optional related data should not block closing.
      }

      onCreated?.({
        id: `${newOrgContact.id || ""}`,
        customerId: `${newOrgContact.customerId || ""}`,
        name: `${newOrgContact.companyName || data.companyName.trim()}`,
      });

      if (isSearch) {
        setContactsForSort?.({
          name: `${newOrgContact.companyName || ""}`,
          id: `${newOrgContact.id || ""}`,
          customerId: `${newOrgContact.customerId || ""}`,
          isBusinessContact: true,
        });
        closeForm();
        return;
      }

      if (newOrgContact.id) {
        navigate(`/organization/${newOrgContact.id}`);
      }
      closeForm();
    } catch {
      setIsSubmittingContact(false);
    }
  };

  const closeForm = useCallback(() => {
    closeModal();
    closeAddCustomerOrganizationPopupModal();
  }, [closeAddCustomerOrganizationPopupModal, closeModal]);

  return (
    <>
      <AddContactFormHeader
        title={"New Organization"}
        onClose={closeAddCustomerOrganizationPopupModal}
      />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={"flex max-h-[calc(100vh-96px)] flex-col"}>
        <div className={"overflow-y-auto px-6 pb-6 pt-4"}>
          {errorAdd && (
            <div
              className={
                "my-2 rounded-md border border-gray-300 p-2 font-semibold text-error"
              }>
              {parseMutationError(errorAdd)}
            </div>
          )}

          <div className={"form-control lg:col-span-5"}>
            <FieldLabel label={"Company Name"} required />
            <Controller
              control={control}
              rules={{
                required: LABELS.required,
                maxLength: {
                  value: 50,
                  message:
                    "Company name shouldn't be greater than 50 characters",
                },
                minLength: {
                  value: 2,
                  message: "Company name should be greater than 1 character",
                },
                validate: (value) => value.trim().length > 0 || LABELS.required,
              }}
              render={({ field: { value, name } }) => (
                <SearchContacts
                  setValue={(newValue) => {
                    setValue(name, newValue.trim(), { shouldValidate: true });
                  }}
                  value={value}
                  placeholder={"Company name"}
                  close={closeForm}
                  removeSelectIcon
                  type={"organization"}
                />
              )}
              name={"companyName"}
            />
            {errors.companyName?.message && (
              <span className={"pt-2 text-sm font-bold text-error"}>
                {errors.companyName.message}
              </span>
            )}
          </div>

          <div className={"form-control mt-5 lg:col-span-5"}>
            <FieldLabel label={region.businessId.label} />
            <SettingsInput
              type={"text"}
              {...register("businessNumber", {
                validate: (value) =>
                  businessIdProblem(region.code, value) || true,
                onChange: (event) => {
                  setDuplicateBusinessNumberError("");
                  const formatted = formatBusinessId(
                    region.code,
                    event.target.value,
                  );
                  if (formatted !== event.target.value)
                    setValue("businessNumber", formatted);
                },
              })}
              variant={"form"}
              focusStyle={"select"}
              size={"md"}
              inputClasses={`w-full ${ADD_CONTACT_INPUT_CLASSES}`}
              placeholder={region.businessId.placeholder}
              maxLength={businessIdMaxLength(region.code)}
              inputMode={region.code === "GB" ? "text" : "numeric"}
              onKeyDown={(event) => {
                // UK company numbers may start with letters (e.g. SC123456).
                if (region.code !== "GB" && !isAllowedNumberBoxKey(event))
                  event.preventDefault();
              }}
              onPaste={(event) => {
                // "123456789 RT0001" keeps the 9-digit BN (QA batch 8);
                // other countries keep their own format.
                event.preventDefault();
                setValue(
                  "businessNumber",
                  formatBusinessId(
                    region.code,
                    event.clipboardData.getData("Text"),
                  ),
                  { shouldValidate: true },
                );
              }}
            />
            {errors.businessNumber?.message && (
              <span className={"pt-2 text-xs font-bold text-error"}>
                {errors.businessNumber.message}
              </span>
            )}
            {duplicateBusinessNumberError && (
              <div className={"text-base font-semibold text-error"}>
                {duplicateBusinessNumberError}
              </div>
            )}
          </div>

          <ContactTypeSelector value={contactType} onChange={setContactType} />

          <AddContactSection
            title={"Primary Contact"}
            description={"Add the main contact person for this organization."}
            className={"mt-6"}>
            <div>
              <FieldLabel
                label={"Contact Person Name"}
                className={"!mb-1 text-xs"}
              />
              <SettingsInput
                type={"text"}
                {...register("primaryContactName", {
                  validate: (value) => personNameProblem(value, 100) || true,
                })}
                variant={"form"}
                focusStyle={"select"}
                size={"md"}
                inputClasses={`w-full ${ADD_CONTACT_INPUT_CLASSES}`}
                placeholder={"Full name"}
              />
              {errors.primaryContactName?.message && (
                <span className={"pt-2 text-xs font-bold text-error"}>
                  {errors.primaryContactName.message}
                </span>
              )}
            </div>
            <div className={"mt-3 grid grid-cols-2 gap-3"}>
              <div>
                <FieldLabel label={"Phone"} className={"!mb-1 text-xs"} />
                <SettingsInput
                  type={"text"}
                  inputMode={"tel"}
                  {...register("primaryContactPhone", {
                    validate: (value) => phoneNumberProblem(value) || true,
                    onChange: (event) => {
                      setValue(
                        "primaryContactPhone",
                        formatPhoneNumber(event.target.value),
                      );
                    },
                  })}
                  variant={"form"}
                  focusStyle={"select"}
                  size={"md"}
                  inputClasses={`w-full ${ADD_CONTACT_INPUT_CLASSES}`}
                  placeholder={"Phone number"}
                />
                {errors.primaryContactPhone?.message && (
                  <span className={"pt-2 text-xs font-bold text-error"}>
                    {errors.primaryContactPhone.message}
                  </span>
                )}
              </div>
              <div>
                <FieldLabel label={"Email"} className={"!mb-1 text-xs"} />
                <SettingsInput
                  type={"text"}
                  {...register("primaryContactEmail", {
                    validate: validateEmail("Invalid email address format"),
                  })}
                  variant={"form"}
                  focusStyle={"select"}
                  size={"md"}
                  inputClasses={`w-full ${ADD_CONTACT_INPUT_CLASSES}`}
                  placeholder={"email@company.com"}
                />
                {errors.primaryContactEmail?.message && (
                  <span className={"pt-2 text-xs font-bold text-error"}>
                    {errors.primaryContactEmail.message}
                  </span>
                )}
              </div>
            </div>
          </AddContactSection>

          <AddContactSection
            title={"Linked Profile"}
            description={"Link another profile for quick access."}>
            <div className={"grid grid-cols-[1.5fr_1fr] gap-3"}>
              <div>
                <FieldLabel label={"Contact"} className={"!mb-1 text-xs"} />
                <Controller
                  control={control}
                  name={"linkedProfile"}
                  render={({ field: { value, onChange } }) => (
                    <SelectContacts
                      value={value}
                      onChange={(data) => onChange(data)}
                    />
                  )}
                />
              </div>

              <div>
                <FieldLabel
                  label={"Relationship"}
                  className={"!mb-1 text-xs"}
                />
                <SettingsInput
                  type={"text"}
                  {...register("linkedProfileRelation", {
                    validate: (value) => relationshipProblem(value) || true,
                  })}
                  variant={"form"}
                  focusStyle={"select"}
                  size={"md"}
                  inputClasses={`w-full ${ADD_CONTACT_INPUT_CLASSES}`}
                  placeholder={"Role"}
                />
                {errors.linkedProfileRelation?.message && (
                  <span className={"pt-2 text-xs font-bold text-error"}>
                    {errors.linkedProfileRelation.message}
                  </span>
                )}
              </div>
            </div>
          </AddContactSection>

          <div className={"mt-5"}>
            <FieldLabel label={"Tags"} />
            <ContactTagsSelector
              value={watch("tags")}
              onChange={(tags) => setValue("tags", tags, { shouldDirty: true })}
            />
          </div>
        </div>

        <AddContactFormFooter
          onCancel={closeAddCustomerOrganizationPopupModal}
          isSubmitting={isSubmittingContact}
        />
      </form>
      {duplicateEmailDialog}
    </>
  );
};

export default AddOrganizationView;
